import PropTypes from "prop-types";
import { useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import {
  NavLink as RouterLink,
  matchPath,
  useLocation,
} from "react-router-dom";
import arrowIosForwardFill from "@iconify/icons-eva/arrow-ios-forward-fill";
import arrowIosDownwardFill from "@iconify/icons-eva/arrow-ios-downward-fill";

// material
import { alpha, useTheme, styled } from "@mui/material/styles";
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  ListItemButton,
} from "@mui/material";
import useAuth from "hooks/useAuth";

const ListSubheaderStyle = styled((props) => (
  <ListSubheader disableSticky disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.overline,
  marginTop: theme.spacing(1.25),
  marginBottom: theme.spacing(0.25),
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  color: theme.palette.text.secondary,
  letterSpacing: "0.08em"
}));

const ListItemStyle = styled(ListItemButton)(
  ({ theme, pad: { paddingLeft = 0 } = {} }) => ({
    ...theme.typography.body2,
    height: 40,
    position: "relative",
    textTransform: "capitalize",
    paddingLeft: theme.spacing(paddingLeft),
    paddingRight: theme.spacing(2.5),
    color: theme.palette.text.secondary,
    borderRadius: 10,
    margin: theme.spacing(0.35, 1.25, 0.35, 1.25),
    transition: theme.transitions.create(["background-color", "color", "box-shadow"]),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.text.primary,
    },
    "&:before": {
      top: 0,
      left: 0,
      width: 4,
      bottom: 4,
      borderRadius: 12,
      marginTop: 4,
      content: "''",
      display: "none",
      position: "absolute",
      backgroundColor: theme.palette.primary.main,
    },
  })
);

const ListItemIconStyle = styled(ListItemIcon)({
  minWidth: 28,
  width: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const getItemIndent = (depth) => 2.8 + (depth - 1) * 2;

NavItem.propTypes = {
  isShow: PropTypes.bool,
  depth: PropTypes.number.isRequired,
  item: PropTypes.object,
  onShortcutDragStart: PropTypes.func,
};

function NavItem({ item, isShow, depth, onShortcutDragStart }) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { title, path, icon, info, children, permissions, isExpanded } = item;
  const isActiveRoot = path
    ? !!matchPath({ path, end: false }, pathname)
    : false;
  const [open, setOpen] = useState(isActiveRoot || isExpanded);
  const { checkAccess } = useAuth();

  useEffect(() => {
    setOpen(isActiveRoot || isExpanded);
  }, [isActiveRoot, isExpanded]);

  const handleOpen = () => {
    setOpen(!open);
  };

  const activeRootStyle = {
    color: "primary.main",
    fontWeight: "fontWeightMedium",
    bgcolor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    "&:before": { display: "block" },
    boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.18)}`,
  };

  const activeSubStyle = {
    color: "text.primary",
    fontWeight: "fontWeightMedium",
    bgcolor: alpha(theme.palette.primary.main, 0.08),
  };

  if (children) {
    return checkAccess(permissions) ? (
      <>
        <ListItemStyle
          onClick={handleOpen}
          pad={{ paddingLeft: getItemIndent(depth) }}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          {depth <= 1 && icon && (
            <ListItemIconStyle>{icon}</ListItemIconStyle>
          )}

          {isShow && (
            <>
              <ListItemText disableTypography primary={title} />
              {info && info}
              <Box
                component={Icon}
                icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                sx={{ width: 16, height: 16, ml: 1 }}
              />
            </>
          )}
        </ListItemStyle>

        {isShow && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((child, index) => {
                const { title: childTitle, path: childPath, children: childChildren } = child;
                const isActiveSub = childPath
                  ? !!matchPath({ path: childPath, end: false }, pathname)
                  : false;
                const newDepth = depth + 1;
                const key = `nav-${child.path || child.title}-${newDepth}-${index}`;

                if (childChildren) {
                  return (
                    <NavItem
                      key={key}
                      item={child}
                      isShow={isShow}
                      depth={newDepth}
                      onShortcutDragStart={onShortcutDragStart}
                    />
                  );
                }

                return checkAccess(child.permissions) ? (
                  <ListItemStyle
                    key={`nav-${childPath || childTitle}-${newDepth}-${index}`}
                    component={RouterLink}
                    pad={{ paddingLeft: getItemIndent(newDepth) + 1.5 }}
                    to={childPath}
                    draggable={Boolean(childPath && onShortcutDragStart)}
                    onDragStart={(event) => {
                      if (!onShortcutDragStart) return;
                      onShortcutDragStart(event, {
                        title: childTitle,
                        path: childPath,
                        permissions: child.permissions || null,
                      });
                    }}
                    sx={{
                      ...(isActiveSub && activeSubStyle),
                    }}
                  >
                    {newDepth <= 1 && child.icon && (
                      <ListItemIconStyle>{child.icon}</ListItemIconStyle>
                    )}

                    <ListItemText disableTypography primary={childTitle} />
                  </ListItemStyle>
                ) : null;
              })}
            </List>
          </Collapse>
        )}
      </>
    ) : null;
  }

  return checkAccess(permissions) ? (
    <ListItemStyle
      component={RouterLink}
      to={path}
      draggable={Boolean(path && onShortcutDragStart)}
      onDragStart={(event) => {
        if (!onShortcutDragStart) return;
        onShortcutDragStart(event, {
          title,
          path,
          permissions: permissions || null,
        });
      }}
      pad={{ paddingLeft: getItemIndent(depth) }}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
    >
      {depth <= 1 && icon && <ListItemIconStyle>{icon}</ListItemIconStyle>}
      {isShow && (
        <>
          <ListItemText disableTypography primary={title} />
          {info && info}
        </>
      )}
    </ListItemStyle>
  ) : null;
}

NavSection.propTypes = {
  isShow: PropTypes.bool,
  navConfig: PropTypes.array,
  searchTerm: PropTypes.string,
  onShortcutDragStart: PropTypes.func,
};

export default function NavSection({ navConfig, isShow = true, searchTerm = "", onShortcutDragStart = null, ...other }) {
  const { checkAccess } = useAuth();
  const permittedConfig = useMemo(() => {
    const filterItems = (items = []) =>
      items.reduce((acc, item) => {
        if (!checkAccess(item.permissions)) return acc;

        if (Array.isArray(item.children) && item.children.length > 0) {
          const children = filterItems(item.children);
          if (children.length === 0) return acc;
          acc.push({ ...item, children });
          return acc;
        }

        acc.push(item);
        return acc;
      }, []);

    return (navConfig || [])
      .map((section) => ({ ...section, items: filterItems(section.items || []) }))
      .filter((section) => (section.items || []).length > 0);
  }, [checkAccess, navConfig]);

  const initialOpenMap = useMemo(() => {
    const map = {};
    permittedConfig.forEach((section, index) => {
      const key = section.subheader || `section-${index}`;
      map[key] = false;
    });
    return map;
  }, [permittedConfig]);
  const [openSections, setOpenSections] = useState(initialOpenMap);

  useEffect(() => {
    const hasSearch = Boolean(searchTerm?.trim());

    if (hasSearch) {
      const searchOpenMap = {};
      permittedConfig.forEach((section, index) => {
        const key = section.subheader || `section-${index}`;
        searchOpenMap[key] = (section.items || []).length > 0;
      });
      setOpenSections(searchOpenMap);
      return;
    }

    setOpenSections(initialOpenMap);
  }, [initialOpenMap, permittedConfig, searchTerm]);

  return (
    <Box {...other}>
      {permittedConfig.map((list, index) => {
        const { subheader, items } = list;
        const sectionKey = subheader || `section-${index}`;
        const isOpen = openSections[sectionKey] ?? false;
        return (
          <List key={`section-${subheader}${index}`} disablePadding>
            {isShow && subheader && (
              <ListItemStyle
                onClick={() =>
                  setOpenSections((prev) => ({ ...prev, [sectionKey]: !isOpen }))
                }
                pad={{ paddingLeft: 1 }}
                sx={{ height: 34 }}
              >
                <ListItemText disableTypography primary={<ListSubheaderStyle>{subheader}</ListSubheaderStyle>} />
                <Box
                  component={Icon}
                  icon={isOpen ? arrowIosDownwardFill : arrowIosForwardFill}
                  sx={{ width: 16, height: 16, ml: 1 }}
                />
              </ListItemStyle>
            )}
            <Collapse in={isShow ? isOpen : true} timeout="auto" unmountOnExit>
              {items.map((item, idx) => (
                <NavItem
                  key={`NAV-${item.path || item.title}-${idx}`}
                  item={item}
                  isShow={isShow}
                  depth={1}
                  onShortcutDragStart={onShortcutDragStart}
                />
              ))}
            </Collapse>
          </List>
        );
      })}
    </Box>
  );
}
